import React, { useMemo, memo, useEffect } from "react";
import Header from "../Components/Header";
import Footer from "../Components/Footer";
import { Truck, Package, Users, Calculator } from "lucide-react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import FooterCta from "../Components/FooterCta";

// Import feature images
import fullLoadImage from "../Assets/full-load-tms.svg";
import partLoadImage from "../Assets/part-load-management.svg";
import fleetManagementImage from "../Assets/fleet-management.svg";
import accountsBillingImage from "../Assets/accounts-billing.svg";

const FeaturePage = () => {
  // ✅ Memoize static data to prevent re-creation
  const featureSections = useMemo(
    () => [
      {
        title: "Full Load TMS",
        icon: Truck,
        image: fullLoadImage,
        features: [
          "Complete trip lifecycle management",
          "Real-time GPS tracking",
          "Automated rate calculations",
          "POD (Proof of Delivery) management",
          "Trip settlement and invoicing",
        ],
      },
      {
        title: "Part Load Management",
        icon: Package,
        image: partLoadImage,
        features: [
          "Multi-consignment handling",
          "Weight and space optimization",
          "Shared load coordination",
          "Individual consignment tracking",
          "Flexible billing options",
        ],
      },
      {
        title: "Fleet Management",
        icon: Users,
        image: fleetManagementImage,
        features: [
          "Vehicle master data management",
          "Driver assignment and tracking",
          "Vehicle maintenance scheduling",
          "Document expiry alerts",
          "Performance analytics",
        ],
      },
      {
        title: "Accounts & Billing",
        icon: Calculator,
        image: accountsBillingImage,
        features: [
          "Automated invoice generation",
          "Payment tracking and reminders",
          "Expense management",
          "GST compliant billing",
          "Comprehensive financial reports",
        ],
      },
    ],
    []
  );

  // ✅ Animation variants
  const fadeUp = {
    hidden: { opacity: 0, y: 40 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: { delay: i * 0.15, type: "spring", stiffness: 60 },
    }),
  };

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.5, ease: "easeOut" },
  };

  const staggerContainer = {
    animate: {
      transition: { staggerChildren: 0.1 },
    },
  };

  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://assets.calendly.com/assets/external/widget.js";
    script.async = true;

    script.onload = () => {
      console.log("✅ Calendly script loaded");
    };

    script.onerror = () => {
      console.error("❌ Failed to load Calendly script");
    };

    document.body.appendChild(script);

    const handleEvent = (e) => {
      if (e?.data?.event === "calendly.event_scheduled") {
        console.log("✅ Meeting Scheduled:", e.data);
      }
    };

    window.addEventListener("message", handleEvent);
    return () => window.removeEventListener("message", handleEvent);
  }, []);

  // ✅ Popup handler with fallback retry
  const handleBookDemo = () => {
    console.log("👆 Book Demo clicked");

    const openPopup = () => {
      if (window.Calendly?.initPopupWidget) {
        window.Calendly.initPopupWidget({
          url: "https://calendly.com/triosoft-support/30min",
        });
        console.log("✅ Calendly Popup Opened");
        return true;
      }
      return false;
    };

    // ✅ Try opening immediately
    if (openPopup()) return;

    // ✅ Retry after delay if script isn't ready
    console.warn("⚠️ Calendly not ready, retrying...");
    setTimeout(() => {
      if (!openPopup()) {
        alert("Calendly is still loading. Please try again in a moment.");
      }
    }, 500);
  };
  return (
    <main className="bg-[var(--page-bg)] text-[var(--black)]">
      <Header />

      {/* ===== Hero Section ===== */}
      <section
        className="relative overflow-hidden bg-[var(--page-bg)] py-10 lg:py-32"
        id="home-hero1"
      >
        <div className="max-w-[900px] mx-auto px-4 lg:px-8">
          <div
            className="grid gap-12 lg:gap-16 items-center"
            style={{ textAlign: "center" }}
          >
            <motion.div
              initial="initial"
              animate="animate"
              variants={staggerContainer}
            >
              <motion.h1
                className="font-heading text-3xl lg:text-6xl font-bold leading-tight mb-6 popingFont"
                variants={fadeInUp}
              >
                All Transport Operations. One Smart System.
              </motion.h1>

              <motion.p
                className="text-md lg:text-lg text-[--gray-text] mb-8"
                variants={fadeInUp}
              >
                MaalWahak offers a powerful suite of features designed for
                Indian transport and logistics businesses — helping you manage
                everything from bookings to billing effortlessly.
              </motion.p>

              <motion.div
                className="flex flex-wrap gap-5"
                style={{ justifySelf: "center" }}
                variants={fadeInUp}
              >
                <motion.button
                  className="second_theme_btn flex items-center"
                  whileTap={{ scale: 0.95 }}
                  onClick={handleBookDemo}
                >
                  Book Demo <ArrowRight className="ml-2 h-5 w-5" />
                </motion.button>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ===== Features Section ===== */}
      <section className="py-14 lg:py-24 relative overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-4 lg:px-8 space-y-20">
          {featureSections.map((section, index) => {
            const Icon = section.icon;
            const isEven = index % 2 === 0;

            return (
              <motion.div
                key={section.title}
                className={`grid lg:gap-24 gap-12 lg:grid-cols-2 items-center ${
                  !isEven ? "lg:grid-flow-dense" : ""
                }`}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                custom={index}
                variants={fadeUp}
              >
                {/* ===== Left: Content ===== */}
                <div
                  className={!isEven ? "lg:col-start-2" : "rightAlign"}
                  // style={{
                  //   textAlign: isDesktop ? " " : isEven ? "right" : " ",
                  // }}
                >
                  <motion.div
                    className="inline-flex h-16 w-16 items-center justify-center rounded-xl bg-[#e9eef4] shadow-sm mb-6"
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    transition={{ type: "spring", stiffness: 200 }}
                  >
                    <Icon className="h-8 w-8 text-[var(--primary-color)]" />
                  </motion.div>

                  <h2 className="font-heading text-3xl font-bold mb-6">
                    {section.title}
                  </h2>

                  <ul className="space-y-4 text-[15px]">
                    {section.features.map((feature, fIndex) => (
                      <motion.li
                        key={fIndex}
                        className={`flex ${
                          isEven ? "items-end flexDirection" : "items-start"
                        } gap-3`}
                        style={{
                          justifyContent: "flex-start",
                          flexDirection: isEven ? "row-reverse" : " ",
                        }}
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{
                          delay: fIndex * 0.05,
                          duration: 0.3,
                          type: "spring",
                        }}
                      >
                        <div className="flex h-6 w-6 items-center justify-center rounded-full bg-[#e9eef4] flex-shrink-0 mt-0.5">
                          <span className="h-2 w-2 rounded-full bg-[var(--primary-color)]"></span>
                        </div>
                        <span>{feature}</span>
                      </motion.li>
                    ))}
                  </ul>
                </div>

                {/* ===== Right: Image Block ===== */}
                <motion.div
                  className={`rounded-2xl p-12 relative overflow-hidden  ${!isEven ? "lg:col-start-1" : ""}`}
                  whileHover={{ scale: 1.02 }}
                  transition={{ type: "spring", stiffness: 200 }}
                >
                  <div className="aspect-square flex items-center justify-center relative">
                    <motion.img
                      src={section.image}
                      alt={section.title}
                      className="w-full h-full object-contain"
                      initial={{ opacity: 0, scale: 0.8 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.5 }}
                    />
                    <motion.div
                      className="absolute inset-0 rounded-2xl pointer-events-none"
                      style={{
                        background:
                          "radial-gradient(circle at 30% 30%, rgba(255,255,255,0.3), transparent 60%)",
                      }}
                      animate={{ scale: [1, 1.05, 1] }}
                      transition={{
                        duration: 3,
                        repeat: Infinity,
                        ease: "easeInOut",
                      }}
                    />
                  </div>
                </motion.div>
              </motion.div>
            );
          })}
        </div>
      </section>
      <FooterCta
        title="Ready to transform your transport business?"
        subtitle="Schedule a demo and see how MaalWahak can help you grow"
      />
      <Footer />
    </main>
  );
};

// ✅ Memoize the whole component
export default memo(FeaturePage);
